import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { IoAdd, IoClose } from "react-icons/io5";

import { handleError } from "src/Common/function";
import FormTypeService from "src/Services/API/formtype.service";

/**
 * Add, rename and retire the form types a form can be categorised as.
 *
 * The five originals are system types shared by every tenant — they are listed
 * but not editable here, because renaming one would change the list under every
 * other tenant. A tenant's own types are fully editable.
 *
 * Retiring is `isActive = false`, never a delete: forms that recorded a type
 * still reference it by id, and a delete would leave them pointing at nothing.
 * A retired type stops being offered for new forms but still resolves to its
 * name on existing ones.
 *
 * Lives on the Templates page rather than its own route for now.
 */
const FormTypeManagerModal = ({ onClose }) => {
  const [formTypes, setFormTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const fetchTypes = () => {
    setLoading(true);
    // Retired types are included so they can be restored from here — the
    // pickers elsewhere ask for active ones only.
    FormTypeService.getAll(true)
      .then((response) => setFormTypes(response.data || []))
      .catch((error) => handleError(error))
      .finally(() => setLoading(false));
  };

  useEffect(fetchTypes, []);

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    FormTypeService.create({ name })
      .then(() => {
        setNewName("");
        toast("Form type added");
        fetchTypes();
      })
      .catch((error) => handleError(error));
  };

  const handleRename = (formType) => {
    const name = editingName.trim();
    if (!name || name === formType.name) {
      setEditingId(null);
      return;
    }
    FormTypeService.update(formType.id, { name })
      .then(() => {
        setEditingId(null);
        toast("Form type renamed");
        fetchTypes();
      })
      .catch((error) => handleError(error));
  };

  const handleToggleActive = (formType) => {
    const action = formType.isActive ? FormTypeService.archive : FormTypeService.restore;
    action(formType.id)
      .then(() => {
        toast(formType.isActive ? "Form type retired" : "Form type restored");
        fetchTypes();
      })
      .catch((error) => handleError(error));
  };

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <OutsideClickHandler onOutsideClick={onClose}>
        <div className="w-[480px] max-h-[80vh] bg-white rounded-[6px] flex flex-col">
          <div className="border-b-[1px] border-borderColor py-3 px-4 flex items-center justify-between">
            <p className="text-[16px] font-OpenSansMedium font-[600] text-titleColor">FORM TYPES</p>
            <IoClose className="cursor-pointer" onClick={onClose} size={18} />
          </div>

          <div className="p-4 flex items-center gap-2 border-b-[1px] border-borderColor">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder="New form type"
              className="flex-1 h-[30px] px-3 border rounded-[6px] text-[12px] font-OpenSansRegular focus:outline-none focus:border-blue-500"
            />
            <button
              onClick={handleCreate}
              disabled={!newName.trim()}
              className="h-[30px] px-3 rounded-[6px] bg-blueColor text-white text-[11px] font-OpenSansRegular font-[600] flex items-center gap-1 disabled:opacity-40"
            >
              <IoAdd size={14} />
              ADD
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && (
              <p className="px-4 py-6 text-[12px] font-OpenSansRegular text-subTitleColor text-center">Loading…</p>
            )}
            {!loading &&
              formTypes.map((formType) => {
                const isSystem = formType.company === null || formType.company === undefined;
                const isEditing = editingId === formType.id;
                return (
                  <div
                    key={formType.id}
                    className="px-4 py-2 border-b border-borderColor flex items-center gap-2"
                  >
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={() => handleRename(formType)}
                        onKeyDown={(e) => e.key === "Enter" && handleRename(formType)}
                        className="flex-1 h-[26px] px-2 border rounded-[4px] text-[12px] font-OpenSansRegular focus:outline-none focus:border-blue-500"
                      />
                    ) : (
                      <span
                        className={`flex-1 text-[13px] font-OpenSansRegular ${
                          formType.isActive ? "text-[#1A2332]" : "text-subTitleColor line-through"
                        }`}
                      >
                        {formType.name}
                      </span>
                    )}

                    {isSystem ? (
                      <span
                        className="rounded-full px-2 py-[2px] text-[10px] font-OpenSansRegular font-[600] bg-[#EEF0F9] text-[#5F6477]"
                        title="Built in — shared by every company, so it cannot be changed here"
                      >
                        Built in
                      </span>
                    ) : (
                      <>
                        {!isEditing && (
                          <button
                            onClick={() => {
                              setEditingId(formType.id);
                              setEditingName(formType.name);
                            }}
                            className="text-[11px] font-OpenSansRegular text-blueColor"
                          >
                            Rename
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleActive(formType)}
                          className="text-[11px] font-OpenSansRegular text-subTitleColor"
                        >
                          {formType.isActive ? "Retire" : "Restore"}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            {!loading && formTypes.length === 0 && (
              <p className="px-4 py-6 text-[12px] font-OpenSansRegular text-subTitleColor text-center">
                No form types
              </p>
            )}
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default FormTypeManagerModal;
